import { createContext } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface DesignTokens {
  // Base spacing scale (aligned with Tailwind's full scale)
  spacing: {
    0: 'p-0';
    1: 'p-1';
    2: 'p-2';
    3: 'p-3';
    4: 'p-4';
    5: 'p-5';
    6: 'p-6';
    8: 'p-8';
    10: 'p-10';
    12: 'p-12';
    16: 'p-16';
    // Legacy aliases for backward compatibility
    none: '';
    xs: 'p-2';
    sm: 'p-3';
    md: 'p-4';
    lg: 'p-6';
    xl: 'p-12';
  };
  margins: {
    0: 'm-0';
    1: 'm-1';
    2: 'm-2';
    3: 'm-3';
    4: 'm-4';
    5: 'm-5';
    6: 'm-6';
    8: 'm-8';
    10: 'm-10';
    12: 'm-12';
    16: 'm-16';
    // Legacy aliases
    none: '';
    xs: 'm-2';
    sm: 'm-3';
    md: 'm-4';
    lg: 'm-6';
    xl: 'm-12';
  };
  gaps: {
    0: 'gap-0';
    1: 'gap-1';
    2: 'gap-2';
    3: 'gap-3';
    4: 'gap-4';
    5: 'gap-5';
    6: 'gap-6';
    8: 'gap-8';
    10: 'gap-10';
    12: 'gap-12';
    16: 'gap-16';
    // Legacy aliases
    none: '';
    xs: 'gap-1';
    sm: 'gap-2';
    md: 'gap-4';
    lg: 'gap-6';
    xl: 'gap-8';
  };
  // Semantic spacing for common use cases
  semantic: {
    // Card spacing
    cardPadding: 'p-4';
    cardPaddingCompact: 'p-3';
    cardPaddingGenerous: 'p-6';
    cardGap: 'gap-4';
    // Section spacing
    sectionGap: 'gap-6';
    sectionPaddingY: 'py-6';
    // Content spacing
    contentGap: 'gap-4';
    contentPaddingY: 'py-4';
    // Item spacing
    itemGap: 'gap-4';
    compactGap: 'gap-2';
    // Form spacing
    formFieldGap: 'gap-4';
    formLabelGap: 'gap-2';
    formButtonMargin: 'mt-4';
    // Table spacing
    tableCellPaddingX: 'px-4';
    tableCellPaddingY: 'py-2';
    tableHeaderPadding: 'px-4 py-2';
    // Navigation spacing
    navItemPaddingX: 'px-4';
    navItemPaddingY: 'py-2';
    navItemGap: 'gap-1';
  };
  cardSizes: {
    xs: 'max-w-xs';
    sm: 'max-w-sm';
    md: 'max-w-md';
    lg: 'max-w-lg';
    xl: 'max-w-xl';
    '2xl': 'max-w-2xl';
    '4xl': 'max-w-4xl';
    full: 'max-w-full';
  };
  shadows: {
    none: '';
    sm: 'shadow-xs hover:shadow-md';
    md: 'shadow-md hover:shadow-lg';
    lg: 'shadow-lg hover:shadow-xl';
    xl: 'shadow-xl hover:shadow-2xl';
    card: 'shadow-md hover:shadow-lg';
    modal: 'shadow-xl';
    elevated: 'shadow-2xl';
  };
  transitions: {
    fast: 'transition-all duration-150';
    normal: 'transition-all duration-200';
    slow: 'transition-all duration-300';
    shadow: 'transition-shadow duration-300';
    colors: 'transition-colors duration-200';
  };
  borderRadius: {
    none: '';
    sm: 'rounded-xs';
    md: 'rounded-md';
    lg: 'rounded-lg';
    xl: 'rounded-xl';
    full: 'rounded-full';
  };
}

interface ThemeContextType {
  theme: Theme;
  effectiveTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  tokens: DesignTokens;
}

const designTokens: DesignTokens = {
  spacing: {
    0: 'p-0',
    1: 'p-1',
    2: 'p-2',
    3: 'p-3',
    4: 'p-4',
    5: 'p-5',
    6: 'p-6',
    8: 'p-8',
    10: 'p-10',
    12: 'p-12',
    16: 'p-16',
    none: '',
    xs: 'p-2',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
    xl: 'p-12',
  },
  margins: {
    0: 'm-0',
    1: 'm-1',
    2: 'm-2',
    3: 'm-3',
    4: 'm-4',
    5: 'm-5',
    6: 'm-6',
    8: 'm-8',
    10: 'm-10',
    12: 'm-12',
    16: 'm-16',
    none: '',
    xs: 'm-2',
    sm: 'm-3',
    md: 'm-4',
    lg: 'm-6',
    xl: 'm-12',
  },
  gaps: {
    0: 'gap-0',
    1: 'gap-1',
    2: 'gap-2',
    3: 'gap-3',
    4: 'gap-4',
    5: 'gap-5',
    6: 'gap-6',
    8: 'gap-8',
    10: 'gap-10',
    12: 'gap-12',
    16: 'gap-16',
    none: '',
    xs: 'gap-1',
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6',
    xl: 'gap-8',
  },
  semantic: {
    cardPadding: 'p-4',
    cardPaddingCompact: 'p-3',
    cardPaddingGenerous: 'p-6',
    cardGap: 'gap-4',
    sectionGap: 'gap-6',
    sectionPaddingY: 'py-6',
    contentGap: 'gap-4',
    contentPaddingY: 'py-4',
    itemGap: 'gap-4',
    compactGap: 'gap-2',
    formFieldGap: 'gap-4',
    formLabelGap: 'gap-2',
    formButtonMargin: 'mt-4',
    tableCellPaddingX: 'px-4',
    tableCellPaddingY: 'py-2',
    tableHeaderPadding: 'px-4 py-2',
    navItemPaddingX: 'px-4',
    navItemPaddingY: 'py-2',
    navItemGap: 'gap-1',
  },
  cardSizes: {
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '4xl': 'max-w-4xl',
    full: 'max-w-full',
  },
  shadows: {
    none: '',
    sm: 'shadow-xs hover:shadow-md',
    md: 'shadow-md hover:shadow-lg',
    lg: 'shadow-lg hover:shadow-xl',
    xl: 'shadow-xl hover:shadow-2xl',
    card: 'shadow-md hover:shadow-lg',
    modal: 'shadow-xl',
    elevated: 'shadow-2xl',
  },
  transitions: {
    fast: 'transition-all duration-150',
    normal: 'transition-all duration-200',
    slow: 'transition-all duration-300',
    shadow: 'transition-shadow duration-300',
    colors: 'transition-colors duration-200',
  },
  borderRadius: {
    none: '',
    sm: 'rounded-xs',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    full: 'rounded-full',
  },
};

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export type { Theme, ThemeContextType, DesignTokens };
export { designTokens };
